import { useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  ENGAGEMENT_OUTCOMES,
  ENGAGEMENT_TYPES,
  useAccountContacts,
  useCreateContactForAccount,
  useCreateEngagementNote,
  type EngagementOutcome,
  type EngagementType,
} from "@/hooks/useEngagementNotes";

interface AddEngagementNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  accountName?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Rep-facing form to log an external engagement touch on an account.
// Engagement = external touches with humans outside our team.
// See docs/ACCOUNT_PAGE_GOLD_SHEET.md "Activity vs Engagement".
// ─────────────────────────────────────────────────────────────────────────────
export function AddEngagementNoteDialog({
  open,
  onOpenChange,
  accountId,
  accountName,
}: AddEngagementNoteDialogProps) {
  const { userProfile } = useAuth();
  const { data: contacts = [] } = useAccountContacts(accountId);
  const createNote = useCreateEngagementNote();
  const createContact = useCreateContactForAccount();

  const [engagementType, setEngagementType] = useState<EngagementType>("outreach");
  const [engagementDate, setEngagementDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [contactId, setContactId] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<EngagementOutcome | null>(null);
  const [detail, setDetail] = useState("");
  const [attendeesText, setAttendeesText] = useState("");

  const [contactComboboxOpen, setContactComboboxOpen] = useState(false);
  const [inlineCreate, setInlineCreate] = useState(false);
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newLinkedin, setNewLinkedin] = useState("");

  const reset = () => {
    setEngagementType("outreach");
    setEngagementDate(new Date().toISOString().slice(0, 10));
    setContactId(null);
    setOutcome(null);
    setDetail("");
    setAttendeesText("");
    setInlineCreate(false);
    setNewFirst("");
    setNewLast("");
    setNewTitle("");
    setNewEmail("");
    setNewLinkedin("");
  };

  const selectedContact = contacts.find((c) => c.id === contactId) || null;

  const handleCreateContact = async () => {
    if (!newFirst.trim() && !newLast.trim()) {
      toast.error("Contact needs at least a first or last name");
      return;
    }
    try {
      const created = await createContact.mutateAsync({
        accountId,
        accountName: accountName ?? null,
        firstName: newFirst.trim(),
        lastName: newLast.trim(),
        jobTitle: newTitle.trim() || null,
        email: newEmail.trim() || null,
        linkedinUrl: newLinkedin.trim() || null,
      });
      setContactId(created.id);
      setInlineCreate(false);
      setContactComboboxOpen(false);
      toast.success("Contact added to account");
    } catch (e: any) {
      toast.error("Failed to create contact", { description: e?.message });
    }
  };

  const handleSave = async () => {
    try {
      const attendees = attendeesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await createNote.mutateAsync({
        accountId,
        contactId,
        engagementType,
        engagementDate,
        attendees: attendees.length ? attendees : null,
        outcome,
        detail: detail.trim() || null,
        performedBy: {
          id: userProfile?.id ?? null,
          name: userProfile?.sales_rep_name || userProfile?.full_name || "User",
        },
      });
      toast.success("Engagement note logged");
      reset();
      onOpenChange(false);
    } catch (e: any) {
      toast.error("Failed to save engagement note", { description: e?.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Log engagement</DialogTitle>
          <DialogDescription>
            Capture an outreach touch, call, meeting, demo, or LinkedIn interaction. This is an external
            engagement — internal system events (note edits, tier changes) go on the Activity tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={engagementDate}
                onChange={(e) => setEngagementDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={engagementType} onValueChange={(v) => setEngagementType(v as EngagementType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENGAGEMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Contact (optional)</Label>
            {!inlineCreate && (
              <Popover open={contactComboboxOpen} onOpenChange={setContactComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn("w-full justify-between font-normal", !selectedContact && "text-muted-foreground")}
                  >
                    {selectedContact?.full_name || "Select contact…"}
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                  <Command>
                    <CommandInput placeholder="Search contacts…" />
                    <CommandList>
                      <CommandEmpty>
                        <div className="py-3 text-sm text-muted-foreground">No matching contact.</div>
                      </CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setContactId(null);
                            setContactComboboxOpen(false);
                          }}
                        >
                          <span className="text-muted-foreground italic">No specific contact</span>
                        </CommandItem>
                        {contacts.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={(c.full_name || "") + " " + (c.job_title || "")}
                            onSelect={() => {
                              setContactId(c.id);
                              setContactComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-3.5 w-3.5",
                                contactId === c.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="text-sm">{c.full_name || "—"}</span>
                              {c.job_title && (
                                <span className="text-[11px] text-muted-foreground">{c.job_title}</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                      <CommandGroup heading="">
                        <CommandItem
                          onSelect={() => {
                            setInlineCreate(true);
                            setContactComboboxOpen(false);
                          }}
                        >
                          <Plus className="mr-2 h-3.5 w-3.5" />
                          Add new contact to this account
                        </CommandItem>
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {inlineCreate && (
              <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">New contact</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={() => setInlineCreate(false)}>
                    Cancel
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="First name" value={newFirst} onChange={(e) => setNewFirst(e.target.value)} />
                  <Input placeholder="Last name" value={newLast} onChange={(e) => setNewLast(e.target.value)} />
                </div>
                <Input placeholder="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  <Input placeholder="LinkedIn URL" value={newLinkedin} onChange={(e) => setNewLinkedin(e.target.value)} />
                </div>
                <Button size="sm" onClick={handleCreateContact} disabled={createContact.isPending}>
                  {createContact.isPending ? "Creating…" : "Add contact"}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Attendees (comma-separated, optional)</Label>
            <Input
              value={attendeesText}
              onChange={(e) => setAttendeesText(e.target.value)}
              placeholder="e.g. Mike Hanson, Jane Doe, Nico"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Outcome (optional)</Label>
            <Select
              value={outcome ?? "none"}
              onValueChange={(v) => setOutcome(v === "none" ? null : (v as EngagementOutcome))}
            >
              <SelectTrigger>
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {ENGAGEMENT_OUTCOMES.map((o) => (
                  <SelectItem key={o} value={o} className="capitalize">
                    {o.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Detail</Label>
            <Textarea
              rows={3}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              placeholder="What happened? Any follow-up?"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createNote.isPending}>
            {createNote.isPending ? "Saving…" : "Log engagement"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
